import type { QuoteFormValues, QuoteView } from "@cx/contracts";
import type { CommercialTermsMode, FreightMode } from "@cx/domain";

// ===== Propostas comerciais: estado do formulário e conversões (RF-VEN-01) =====

/** Linha do formulário. `listPriceCents` nulo em produto = preço da lista (o servidor resolve e congela ao salvar). */
export type ItemRow = {
  key: string;
  productId: string | null;
  description: string;
  code: string | null;
  unit: string;
  qtyQ4: number | null;
  listPriceCents: number | null;
  discountPct4: number | null;
};

export type QuoteForm = {
  date: string;
  validUntil: string;
  contact: { id: string; name: string } | null;
  attentionOf: string;
  sellerId: string;
  priceListId: string;
  introduction: string;
  nextContactAt: string;
  items: ItemRow[];
  /** Desconto geral em valor ou em % do subtotal. */
  discountMode: "value" | "pct";
  discountCents: number | null;
  discountPct4: number | null;
  freightCents: number | null;
  otherExpensesCents: number | null;
  commercialTermsMode: CommercialTermsMode;
  paymentTermId: string;
  commercialTermsText: string;
  generalTerms: string;
  carrierId: string;
  freightMode: FreightMode;
};

let seq = 0;
export const rowKey = () => `r${++seq}`;

export function emptyQuoteForm(today: string, sellerId = ""): QuoteForm {
  return {
    date: today,
    validUntil: "",
    contact: null,
    attentionOf: "",
    sellerId,
    priceListId: "",
    introduction: "",
    nextContactAt: "",
    items: [],
    discountMode: "value",
    discountCents: null,
    discountPct4: null,
    freightCents: null,
    otherExpensesCents: null,
    commercialTermsMode: "none",
    paymentTermId: "",
    commercialTermsText: "",
    generalTerms: "",
    carrierId: "",
    freightMode: 9,
  };
}

/** Proposta gravada → formulário de edição. Os preços vêm congelados (explícitos). */
export function formFromView(q: QuoteView): QuoteForm {
  return {
    date: q.date,
    validUntil: q.validUntil ?? "",
    contact: { id: q.contact.id, name: q.contact.name },
    attentionOf: q.attentionOf ?? "",
    sellerId: q.seller?.id ?? "",
    priceListId: q.priceList?.id ?? "",
    introduction: q.introduction ?? "",
    nextContactAt: q.nextContactAt ?? "",
    items: q.items.map((i) => ({
      key: rowKey(),
      productId: i.productId,
      description: i.description,
      code: i.code,
      unit: i.unit ?? "",
      qtyQ4: i.qtyQ4,
      listPriceCents: i.listPriceCents,
      discountPct4: i.discountPct4,
    })),
    discountMode: q.discountPct4 !== null ? "pct" : "value",
    discountCents: q.discountPct4 !== null ? null : q.discountCents,
    discountPct4: q.discountPct4,
    freightCents: q.freightCents,
    otherExpensesCents: q.otherExpensesCents,
    commercialTermsMode: q.commercialTermsMode,
    paymentTermId: q.paymentTerm?.id ?? "",
    commercialTermsText: q.commercialTermsText ?? "",
    generalTerms: q.generalTerms ?? "",
    carrierId: q.carrier?.id ?? "",
    freightMode: q.freightMode,
  };
}

const orNull = (s: string) => (s.trim() ? s.trim() : null);

/** Formulário → corpo de POST/PUT /quotes (o servidor valida tudo de novo). */
export function quoteBody(f: QuoteForm): QuoteFormValues {
  return {
    date: f.date,
    validUntil: orNull(f.validUntil),
    contactId: f.contact?.id ?? "",
    attentionOf: orNull(f.attentionOf),
    sellerId: orNull(f.sellerId),
    priceListId: orNull(f.priceListId),
    introduction: orNull(f.introduction),
    nextContactAt: orNull(f.nextContactAt),
    items: f.items.map((i) => ({
      productId: i.productId,
      description: i.productId ? null : orNull(i.description),
      unit: i.productId ? null : orNull(i.unit),
      qtyQ4: i.qtyQ4 ?? 0,
      listPriceCents: i.listPriceCents,
      discountPct4: i.discountPct4 ?? 0,
    })),
    discountPct4: f.discountMode === "pct" ? (f.discountPct4 ?? 0) : null,
    discountCents: f.discountMode === "value" ? (f.discountCents ?? 0) : 0,
    freightCents: f.freightCents ?? 0,
    otherExpensesCents: f.otherExpensesCents ?? 0,
    commercialTermsMode: f.commercialTermsMode,
    paymentTermId: f.commercialTermsMode === "installments" ? orNull(f.paymentTermId) : null,
    commercialTermsText: f.commercialTermsMode === "text" ? orNull(f.commercialTermsText) : null,
    generalTerms: orNull(f.generalTerms),
    carrierId: orNull(f.carrierId),
    freightMode: f.freightMode,
  };
}

/**
 * Corpo da prévia (totais e parcelas): só o que entra na conta. Null enquanto falta cliente ou item, ou há linha
 * incompleta (a prévia não acusa erro de campo que o usuário ainda está preenchendo).
 */
export function previewBody(f: QuoteForm): QuoteFormValues | null {
  if (!f.contact || f.items.length === 0) return null;
  if (f.items.some((i) => !i.qtyQ4 || (!i.productId && (!i.description.trim() || i.listPriceCents === null))))
    return null;
  const b = quoteBody(f);
  const installments = f.commercialTermsMode === "installments" && !!f.paymentTermId;
  return {
    ...b,
    attentionOf: null,
    introduction: null,
    generalTerms: null,
    validUntil: null,
    nextContactAt: null,
    commercialTermsMode: installments ? "installments" : "none",
    paymentTermId: installments ? f.paymentTermId : null,
    commercialTermsText: null,
  };
}

/** Por que o botão "Converter em pedido" está desabilitado (null = pode converter). */
export function convertBlockReason(q: Pick<QuoteView, "canConvert" | "convertedOrderId" | "status">) {
  if (q.canConvert) return null;
  if (q.convertedOrderId) return "Esta proposta já foi convertida em pedido.";
  if (q.status.baseState === "canceled") return "Proposta não aprovada não pode ser convertida.";
  return "A conversão fica disponível com o módulo de pedidos de venda.";
}

/** Erros de campo da API → chaves do formulário (`items.0.qtyQ4` fica na linha 0). */
export function itemError(errors: Record<string, string>, index: number): string | undefined {
  const prefix = `items.${index}.`;
  const k = Object.keys(errors).find((e) => e.startsWith(prefix));
  return k ? errors[k] : undefined;
}
